/**
 * 4-color DVD-plane quantization and PackBits RGB TIFF encoding
 * for Daikin Scenarist Script image export.
 */

import { SCENARIST_DEFAULT_PLANES } from "./generateSst.js";

const NEAR_PLANE_THRESHOLD_SQ = 48 * 48;

function distSq(r, g, b, plane) {
    const dr = r - plane[0];
    const dg = g - plane[1];
    const db = b - plane[2];
    return dr * dr + dg * dg + db * db;
}

/**
 * Snap each RGB24 pixel to BG, PA, or E1 when close; remaining antialias
 * pixels become E2 (red in the MacCaption convention).
 */
export function quantizeDvdPlanes(rgbBuffer, planes = SCENARIST_DEFAULT_PLANES) {
    const src = rgbBuffer instanceof Uint8Array ? rgbBuffer : Uint8Array.from(rgbBuffer);
    const out = new Uint8Array(src.length);
    const bg = planes.BG;
    const pa = planes.PA;
    const e1 = planes.E1;
    const e2 = planes.E2;

    for (let i = 0; i < src.length; i += 3) {
        const r = src[i];
        const g = src[i + 1];
        const b = src[i + 2];
        const dBg = distSq(r, g, b, bg);
        const dPa = distSq(r, g, b, pa);
        const dE1 = distSq(r, g, b, e1);
        let plane = e2;
        const nearest = Math.min(dBg, dPa, dE1);
        if (nearest <= NEAR_PLANE_THRESHOLD_SQ) {
            if (nearest === dBg) {
                plane = bg;
            } else if (nearest === dPa) {
                plane = pa;
            } else {
                plane = e1;
            }
        }
        out[i] = plane[0];
        out[i + 1] = plane[1];
        out[i + 2] = plane[2];
    }

    return out;
}

function packBitsRow(row) {
    const out = [];
    let i = 0;
    while (i < row.length) {
        if (i + 1 < row.length && row[i] === row[i + 1]) {
            let run = 1;
            while (i + run < row.length && row[i] === row[i + run] && run < 128) {
                run += 1;
            }
            out.push((256 - (run - 1)) & 0xff, row[i]);
            i += run;
        } else {
            const start = i;
            i += 1;
            while (i < row.length && i - start < 128) {
                if (i + 1 < row.length && row[i] === row[i + 1]) {
                    break;
                }
                i += 1;
            }
            const len = i - start;
            out.push(len - 1);
            for (let j = start; j < i; j += 1) {
                out.push(row[j]);
            }
        }
    }
    return out;
}

function writeU16BE(buffer, offset, value) {
    buffer[offset] = (value >> 8) & 0xff;
    buffer[offset + 1] = value & 0xff;
}

function writeU32BE(buffer, offset, value) {
    buffer[offset] = (value >>> 24) & 0xff;
    buffer[offset + 1] = (value >>> 16) & 0xff;
    buffer[offset + 2] = (value >>> 8) & 0xff;
    buffer[offset + 3] = value & 0xff;
}

function writeIfdEntry(buffer, offset, tag, type, count, valueOrOffset) {
    writeU16BE(buffer, offset, tag);
    writeU16BE(buffer, offset + 2, type);
    writeU32BE(buffer, offset + 4, count);
    writeU32BE(buffer, offset + 8, valueOrOffset);
}

/**
 * Encode RGB24 pixels as a big-endian PackBits TIFF (matching the customer sample).
 */
export function encodeRgbTiffPackBits(rgbBuffer, width, height) {
    const expected = width * height * 3;
    if (!rgbBuffer || rgbBuffer.length !== expected) {
        throw new Error(
            `TIFF RGB buffer length ${rgbBuffer?.length ?? 0} does not match ${width}x${height}`,
        );
    }

    const packedRows = [];
    let stripByteCount = 0;
    for (let y = 0; y < height; y += 1) {
        const start = y * width * 3;
        const row = rgbBuffer.subarray
            ? rgbBuffer.subarray(start, start + width * 3)
            : rgbBuffer.slice(start, start + width * 3);
        const packed = packBitsRow(row);
        packedRows.push(packed);
        stripByteCount += packed.length;
    }

    const SHORT = 3;
    const LONG = 4;
    const RATIONAL = 5;
    const tagCount = 12;
    const headerSize = 8;
    const ifdSize = 2 + tagCount * 12 + 4;
    const extraSize = 6 + 8 + 8; // BitsPerSample + two rationals
    const extraOffset = headerSize + ifdSize;
    const stripOffset = extraOffset + extraSize;
    const fileSize = stripOffset + stripByteCount;

    const out = Buffer.alloc(fileSize);
    out[0] = 0x4d;
    out[1] = 0x4d;
    writeU16BE(out, 2, 42);
    writeU32BE(out, 4, headerSize);

    writeU16BE(out, headerSize, tagCount);
    let entry = headerSize + 2;
    const bitsPerSampleOffset = extraOffset;
    const xResOffset = extraOffset + 6;
    const yResOffset = extraOffset + 14;

    writeIfdEntry(out, entry, 256, SHORT, 1, width << 16);
    entry += 12;
    writeIfdEntry(out, entry, 257, SHORT, 1, height << 16);
    entry += 12;
    writeIfdEntry(out, entry, 258, SHORT, 3, bitsPerSampleOffset);
    entry += 12;
    writeIfdEntry(out, entry, 259, SHORT, 1, 32773 << 16);
    entry += 12;
    writeIfdEntry(out, entry, 262, SHORT, 1, 2 << 16);
    entry += 12;
    writeIfdEntry(out, entry, 273, LONG, 1, stripOffset);
    entry += 12;
    writeIfdEntry(out, entry, 277, SHORT, 1, 3 << 16);
    entry += 12;
    writeIfdEntry(out, entry, 278, SHORT, 1, height << 16);
    entry += 12;
    writeIfdEntry(out, entry, 279, LONG, 1, stripByteCount);
    entry += 12;
    writeIfdEntry(out, entry, 282, RATIONAL, 1, xResOffset);
    entry += 12;
    writeIfdEntry(out, entry, 283, RATIONAL, 1, yResOffset);
    entry += 12;
    writeIfdEntry(out, entry, 284, SHORT, 1, 1 << 16);
    entry += 12;
    writeU32BE(out, entry, 0);

    writeU16BE(out, bitsPerSampleOffset, 8);
    writeU16BE(out, bitsPerSampleOffset + 2, 8);
    writeU16BE(out, bitsPerSampleOffset + 4, 8);
    writeU32BE(out, xResOffset, 1);
    writeU32BE(out, xResOffset + 4, 1);
    writeU32BE(out, yResOffset, 1);
    writeU32BE(out, yResOffset + 4, 1);

    let cursor = stripOffset;
    for (const packed of packedRows) {
        for (let i = 0; i < packed.length; i += 1) {
            out[cursor + i] = packed[i];
        }
        cursor += packed.length;
    }

    return out;
}

export function rgb24ToRgba(rgbBuffer, width, height) {
    const rgba = new Uint8ClampedArray(width * height * 4);
    for (let i = 0, j = 0; i < rgbBuffer.length; i += 3, j += 4) {
        rgba[j] = rgbBuffer[i];
        rgba[j + 1] = rgbBuffer[i + 1];
        rgba[j + 2] = rgbBuffer[i + 2];
        rgba[j + 3] = 255;
    }
    return rgba;
}
